import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { adminCommonAreasAttributesService, AdminCommonAreaAttribute } from '@/services/adminCommonAreasAttributesService';
import { AddCommonAreaAttributeModal } from '@/components/Admin/AddCommonAreaAttributeModal';
import { ConfirmDialog } from '@/components/Admin/ConfirmDialog';
import { useParams } from 'react-router-dom';

export function AdminPartiesCommunesDevicesPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId?: string }>();
  const projectIdInt = projectId ? Number(projectId) : null;

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; attribute: AdminCommonAreaAttribute | null }>({
    isOpen: false,
    attribute: null,
  });

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Fetch project
  const {
    data: project,
    isLoading: projectLoading,
  } = useQuery({
    queryKey: ['project', projectIdInt],
    queryFn: async () => {
      // Use projectService if available, or fetch via API
      try {
        const response = await fetch(`/api/projects/${projectIdInt}`);
        return await response.json();
      } catch (e) {
        return null;
      }
    },
    enabled: projectIdInt !== null,
  });

  // Fetch configured attributes for the project
  const {
    data: attributes = [],
    isLoading: isLoadingAttributes,
    error: attributesError,
  } = useQuery({
    queryKey: ['admin-common-areas-attributes', projectIdInt],
    queryFn: () => adminCommonAreasAttributesService.getAttributes(projectIdInt as number),
    enabled: projectIdInt !== null,
  });

  const removeMutation = useMutation({
    mutationFn: (attributeId: number) =>
      adminCommonAreasAttributesService.removeAttribute(projectIdInt as number, attributeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-common-areas-attributes', projectIdInt] });
      setDeleteConfirm({ isOpen: false, attribute: null });
    },
  });

  const handleDeleteClick = (attribute: AdminCommonAreaAttribute) => {
    setDeleteConfirm({ isOpen: true, attribute });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.attribute) {
      removeMutation.mutate(deleteConfirm.attribute.id);
    }
  };

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Configuration</span> / Attribute data / Partie commune
          </div>
          {project && (
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
              {project.city && (
                <>
                  <span className="text-slate-400">•</span>
                  <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                </>
              )}
            </div>
          )}
          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
            <div className="h-9 w-9 rounded-full bg-gradient-to-tr from-primary to-purple-500 flex items-center justify-center text-white text-xs font-bold">
              {user?.fullName ? getUserInitials(user.fullName) : 'AD'}
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Parties communes</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Configurez les attributes à afficher dans les parties communes pour les utilisateurs.
              </p>
            </div>
            {projectIdInt && (
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors whitespace-nowrap"
              >
                <span className="material-icons-outlined" style={{ fontSize: '20px' }}>
                  add
                </span>
                Ajouter
              </button>
            )}
          </div>

          {attributesError && (
            <div className="mb-6 p-4 rounded-lg border border-rose-300/40 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20">
              Erreur lors du chargement. Vérifiez les permissions et réessayez.
            </div>
          )}

          <div className="bg-white dark:bg-[#16202A] border border-slate-200 dark:border-[#1E293B] rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 dark:border-[#1E293B] flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900 dark:text-white">Attributes configurés</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {isLoadingAttributes ? 'Chargement…' : `${attributes.length} attribute(s) configuré(s)`}
                </div>
              </div>
              {isLoadingAttributes && (
                <span className="material-icons-outlined animate-spin text-primary" style={{ fontSize: '20px' }}>
                  progress_activity
                </span>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 dark:bg-[#0B1116] text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left font-medium px-5 py-3">Attribute</th>
                    <th className="text-left font-medium px-5 py-3">Bâtiment</th>
                    <th className="text-left font-medium px-5 py-3">Box</th>
                    <th className="text-left font-medium px-5 py-3">Device</th>
                    <th className="text-left font-medium px-5 py-3">Type</th>
                    <th className="text-right font-medium px-5 py-3">Valeur</th>
                    <th className="text-right font-medium px-5 py-3">Date/Heure</th>
                    <th className="text-right font-medium px-5 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {attributes.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-5 py-8 text-center text-slate-500 dark:text-slate-400">
                        Aucun attribute configuré. Cliquez sur "Ajouter" pour en ajouter.
                      </td>
                    </tr>
                  ) : (
                    attributes.map((attr: AdminCommonAreaAttribute) => (
                      <tr key={attr.id} className="border-t border-slate-100 dark:border-slate-800">
                        <td className="px-5 py-4">
                          <div className="font-semibold text-slate-900 dark:text-white">{attr.name}</div>
                          {attr.uuid && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">UUID: {attr.uuid}</div>
                          )}
                        </td>
                        <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                          {attr.building?.name ?? attr.building?.code ?? `#${attr.building?.id}` ?? '—'}
                        </td>
                        <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                          {attr.box?.name ?? attr.box?.code ?? `#${attr.box?.id}` ?? '—'}
                        </td>
                        <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                          {attr.device?.name ?? attr.device?.code ?? `#${attr.device?.id}` ?? '—'}
                        </td>
                        <td className="px-5 py-4 text-slate-700 dark:text-slate-300">
                          {attr.type?.name ?? attr.type?.code ?? '—'}
                          {attr.type?.unit && (
                            <span className="text-xs text-slate-500 dark:text-slate-400 ml-1">({attr.type.unit})</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {attr.currentValue ?? '—'}
                            {attr.currentValue && attr.type?.unit && (
                              <span className="text-sm text-slate-500 dark:text-slate-400 ml-1">{attr.type.unit}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-right text-slate-500 dark:text-slate-400">
                          {formatDateTime(attr.currentValueTs)}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(attr)}
                            disabled={removeMutation.isPending}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Retirer"
                          >
                            <span className="material-icons-outlined" style={{ fontSize: '20px' }}>delete</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {projectIdInt && (
          <AddCommonAreaAttributeModal
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            projectId={projectIdInt}
            onAdded={() => {
              queryClient.invalidateQueries({ queryKey: ['admin-common-areas-attributes', projectIdInt] });
            }}
          />
        )}

        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, attribute: null })}
          onConfirm={handleDeleteConfirm}
          title="Retirer l'attribute des parties communes ?"
          message={`Êtes-vous sûr de vouloir retirer l'attribute "${deleteConfirm.attribute?.name}" des parties communes ? Cette action peut être annulée en le rajoutant.`}
          confirmText="Retirer"
          cancelText="Annuler"
          variant="danger"
          isLoading={removeMutation.isPending}
        />
      </main>
    </div>
  );
}
